import { IActivity, IActivityCondition, IActivityGroup } from '@azavista/activity-lib';
import { SearchParams } from '@azavista/advanced-search';
import { FieldType } from '@azavista/advanced-search/lib/field-types';
import { EventPublish, FieldComponentType, FieldMode, FieldOperator, RatingFieldOptionVisualStyle } from './enums';
import { CartItemProduct, DonationProductItem, Order, Product, Transaction, TransactionItem } from '@azavista/payments';
import { MultiRegistrationConfig } from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';

export interface IApiServiceConfig {
    baseUrl: string;
    contactBaseUrl: string;
    threeDotOneBaseUrl: string;
    paymentIntegrationWebhookBaseUrl: string;
    client_id: string;
    eventId: string;
}

export interface IApiServiceInitResult {
    identityToken: IStoredIdentityToken;
    eventTokens: IStoredEventToken[];
}

export interface ICreatedAtUpdatedAt {
    created_at?: string;
    updated_at?: string;
}

export interface IStringIdEntity extends ICreatedAtUpdatedAt {
    id?: string;
}

export interface IEventPageResponse {
    id: string;
    name?: string;
    css: string;
    html: string;
    // js: string;
    stylesheet_url: string;
    menu: IMenuItem[];
    translations: { [key: string]: any };
    dependencies?: IDependency[];
    participant?: IObjectWithId;
    event?: IObjectWithId;
    utmcode?: string[];
}

export interface IOperationInfo {
    url: string;
    method: string;
    body: any;
    caller: string;
}

export class ApiError {
    thrownError: any;
    communicationError: ICommunicationError;
}

export interface ICommunicationError {
    message: string;
    status: number;
    statusText: string;
    body?: {
        code?: string;
        message?: string;
        error?: string;
        error_description?: string;
    };
}

export interface IErrorNotification extends IOperationInfo {
    error: ApiError;
}

export interface ISuccessNotification extends IOperationInfo {
    result: any;
}

export interface IGetIdentityTokenRequest {
    email: string;
    registrationCode: string;
}

export interface IGetIdentityTokenResponse {
    token: IIdentityTokenPayload;
}

export interface IGetEventTokenResponse {
    token: IEventTokenPayload;
}

export interface IAuthenticateResponse {
    token: ITokenPayload;
}

export interface ITokenPayload {
    /**
     * User id
     */
    u: number;
    /**
     * 2.0 User id
     */
    l: string;
    /**
     * Customer id
     */
    c: number;
    /**
     * Whether this user is admin
     */
    g: boolean;
    /**
     * Team ids with their scopes array
     */
    s: { [key: string]: string[] };
    /**
     * 2.0 scopes
     */
    az2: string[];
    /**
     * Client id
     */
    cl: string;
    /**
     * Token type (auth, refresh etc.)
     */
    t: string;
    /**
     * Token issued time
     */
    iat: number;
    /**
     * Token expiration time
     */
    exp: number;
}

export interface IEventTokenPayload {
    /**
     * Client id (AZAVISTA-EVENT)
     */
    cl: string;
    /**
     * Token type (auth, refresh etc.)
     */
    t: string;
    username: string;
    event_id: string;
    /**
     * Token expiration time
     */
    exp: number;
    /**
     * Token issued time
     */
    iat: number;
    participant_id?: string;
    contact_id?: string;
    /**
     * This will be set only if token for sepcific page was issued by the server
     */
    page_id?: string;
    /**
     * Organization manager
     */
    om?: boolean;
}

export interface ILinkTokenPayload {
    linktoken: string;
    email: string;
    cl: string;
    t: string;
    entity_id?: string;
    iat: number;
    exp: number;
}

export interface ITokenInfo<TPayload> {
    value: string;
    header: ITokenHeader;
    payload: TPayload;
}

export interface ITokenHeader {
    alg: string;
    typ: string;
}

export interface ITokenInfo<TPayload> {
    value: string;
    header: ITokenHeader;
    payload: TPayload;
}

export interface ITokenHeader {
    alg: string;
    typ: string;
}

export interface IIdentityTokenPayload {
    /**
     * Username (email)
     */
    username: string;
    /**
     * Client id
     */
    cl: string;
    /**
     * Token type (auth, refresh etc.)
     */
    t: string;
    /**
     * Token issued time
     */
    iat: number;
    /**
     * Token expiration time
     */
    exp: number;
    /**
     * This will be set only if token for sepcific page was issued by the server
     */
    page_id?: string;
}

export interface ISchema {
    type: string | string[];
    format?: string;
    enum?: any[];
    minimum?: any;
    maximum?: any;
    multipleOf?: number;
    formatMinimum?: string;
    formatMaximum?: string;
    minLength?: number;
    maxLength?: number;
    items?: { type: string, enum?: any[] };
    default?: any;
}

export interface IAttributeTranslation {
    value: string;
    trans: IValueTranslation;
}

export interface IValueTranslation {
    [key: string]: string;
}

export interface IField {
    id: string;
    name: string;
    label: string;
    category: string;
    builtin: boolean;
    type?: FieldType;
    editable: boolean;
    schema: ISchema;
    timezone?: string;
    component?: FieldComponentType;
    mandatory_for_planners?: boolean;
    locked_for_editing?: boolean;
    visible_for_planners?: boolean;
    options?: { [key: string]: any };
    default_field?: boolean;
    update_participants?: boolean;
    crm_field_id?: string;
    attributeTranslations?: IAttributeTranslation[];
    tool_tip?: string;
    placeholder?: string;
    error_message?: string;
}

export interface IImageFieldOptions {
    cropping_mandatory: boolean;
    aspect_ratio: number;
    maxWidth: number;
    maxHeight: number;
}

// export type InputFieldType =
//     'string' | 'email' | 'number' | 'integer' | 'date' | 'date-time' | 'time' |
//     'boolean' | 'object' | 'enum' | 'array' | 'document' | 'image' |
//     'custom';

export type InputFieldType =
    FieldType |
    'virtual-autocomplete' |
    'virtual-object_enum' |
    'virtual-relation' |
    'virtual-boolean_string' |
    // 'virtual-timezone' |
    'virtual-textarea';

export interface IValidationResult {
    value: any;
    isValid: boolean;
    isRequiredButNotProvided: boolean;
    errorsText: string;
}

export interface ICriteria {
    field: { id: string };
    operator: FieldOperator;
    value: any;
}

export const enum DependencyVersion {
    v1 = 'v1',
    v2 = 'v2',
}

export interface IDependency {
    id: string;
    name: string;
    version?: DependencyVersion;
    criteria: ICriteria[][];
}

export interface IGetDependenciesRequest extends IPagedRequest {
}

export interface IPagedRequest {
    offset: number;
    limit: number;
}

export interface IGetDependenciesResponse extends IPagedResponse {
    dependencies: IDependency[];
}

export interface IGetEventFieldsResponse extends IPagedResponse {
    fields: IField[];
}

export interface IGetEventParticipantFieldsResponse extends IPagedResponse {
    fields: IField[];
}

export interface IGetEventParticipantResponse extends IEventTokenResponse {
    participant: IObjectWithId;
}

export interface ICreateEventParticipantResponse extends IEventTokenResponse, IObjectWithId {
    // participant: IObjectWithId;
}

export interface IUpdateEventParticipantResponse extends IEventTokenResponse {
    // The server no longer returns the updated participant object
    // participant: IObjectWithId;
}

export interface IObjectWithId {
    id: string;
    [key: string]: any;
}

// Some of the responses will contain token field with the event token which must be used further
export interface IEventTokenResponse {
    token: IGetTokenResponse;
}

export interface IGetProcessFormResponse extends IEventTokenResponse {
    form_id: string;
    next_form_id?: string;
    cancellation_workflow_id?: string;
    reservation_type?: 'waitinglist' | null,
    payment_flow?: boolean;
    workflow_type: string;
    multiregistration?: MultiRegistrationConfig;
}

export interface ICompleteProcessFormResponse {
    next_form_id?: string;
    next_page_id?: string;
    thank_you_page_id?: string;
}

export interface ICancelProcessResponse {
    next_page_id?: string;
}

export interface IGetPaymentThankYouPageResponse {
    next_page_id?: string;
}

export interface IStoredIdentityToken {
    email: string;
    tokenResponse: IGetTokenResponse;
}

export interface IStoredEventToken {
    eventId: string;
    tokenResponse: IGetTokenResponse;
}

export interface IEventSettings {
    url: string;
    access: EventAccess;
    login?: LoginMethod[] | null;
    access_code?: string | null;
    identity_provider?: string[] | null;
    authorization: 'code' | 'link';
    languages: ILanguageItem[];
    datetime_format: 'DD-MM-YYYY HH:mm:ss' | 'MM-DD-YYYY h:mm:ss A' | 'LLL' | 'lll' | 'LLLL' | 'llll';
    date_format: 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'L' | 'l' | 'LL' | 'll';
    time_format: 'hh:mm:ss A' | 'HH:mm' | 'LT' | 'LTS';
    modules: string[];
    publish: EventPublish;
    last_registration_date?: string;
    publication_date?: string;
    offline_date?: string;
    participant_teams?: boolean;
    duplicate_email: boolean;
    start: string;
    end: string;
    timezone: string;
    name: string;
    customer_id: number;
}
export interface ILanguageItem {
    id: string;
    label: string;
}

export interface IPagedResponse {
    offset: number;
    object_count: number;
    limit: number;
}

export interface IGetTokenResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
}

export interface IMenuItem {
    type: 'aza-page' | 'aza-form';
    page_id?: string;
    href?: string;
    label: string;
    translatable: string;
}

export interface IFieldModeQuery {
    field_mode: FieldMode;
}

export interface IFieldWithInputElement {
    field: IField;
    element: HTMLElement;
}

export interface IMergetagPosition {
    start: number;
    length: number;
    mergetag: string;
}

export interface IJavascriptIntegrationScript {
    id: string;
    automation_type: string;
    settings: any;
}

export interface IStandardEntity {
    id: string;
    name: string;
    updated_at: string;
    created_at: string;
}

export interface IPaymentProductResponse {
    cancellation_terms: IPaymentProductCancellationTermItem[];
    pay_by_invoice: boolean;
    /**
     * @deprecated this product list should only be used to initiate the `ShoppingCartProcessorUI` or `AppComponent.shoppingCartProductsVisibility`.
     *
     * Otherwise, please use `product` property from `ShoppingCartV2Service.computeCartItems()`
     *
     * https://az2019.azavista.com/product-management/frontend/azavista3.0/-/issues/4461
     * **/
    products: IPaymentProduct[];
    taxes: IPaymentTax[];
}

export interface IPaymentProductCancellationTermData {
    from: number;
    percentage: number;
}

export interface IPaymentProductCancellationTermItem extends IStandardEntity {
    terms: IPaymentProductCancellationTermData[];
}

export type ICartItemProduct = CartItemProduct & {
    product: IPaymentProduct
}

export interface IPaymentProduct extends Product {
    cancellation_terms_id: string;
    category: string;
    currency: string;
    dependency_id?: string;
    dependency_ids?: string[];
    name: string;
    /** @defaultValue 0 */
    min_per_buyer: number;
    max_per_buyer: number;
    more_than_one: boolean;
    price: number;
    tax_id: string;
    type: ProductType;
}

export interface IPaymentTax extends IStandardEntity {
    percentage: number;
}

// Discounts
export const enum DiscountStatus {
    active = 'active',
    inactive = 'inactive'
}

export const enum DiscountType {
    percentage = 'percentage'
}

export interface IDiscount {
    id: string;
    status: DiscountStatus;
    discount_type: DiscountType;
    global: boolean;
    discount_code: string;
    value: number;
    created_at: string;
    upated_at: string;
}

export interface ICheckDiscountCodeResponse {
    discount_code_valid: boolean;
    discount?: IDiscount;
}

export interface ICheckDiscountCodePayload {
    discount_code: string;
}

export interface IProductIdWithQuantity {
    product_id: string;
    quantity: number;
    type: ProductType;
    name: string;
}

export interface IPayProductsPayload {
    products: IProductIdWithQuantity[];
    discount_code?: string;
    pay_by_invoice: boolean;
}

export interface IPayProductsV2CartItem {
    quantity: number;
    product_id: string;
}

export interface IPayProductsV2Payload {
    discount_code: string | null;
    pay_by_invoice: boolean;
    dependencies: IDependency[];
    cartItems: IPayProductsV2CartItem[];
    /** donation-products price  */
    products: DonationProductItem[]
}

export const enum PaymentProvider {
    cybersource = 'cybersource',
    stripe = 'stripe',
    invoice = 'invoice',
    moneris = 'moneris',
    ingenico = 'ingenico',
    payOne = 'payone',
    revere = 'revere',
    mollie = 'mollie',
}
export interface IPayResponse {
    is_checkout: boolean;
    provider: PaymentProvider;
    order_id: string;
    settings: PayResponseSettings;
    transaction_id: string;
}

export interface ICyberSourcePaySettings {
    redirect_url: string;
    cybersource_form: { [key: string]: any };
}

export interface IStripePaySettings {
    public_api_key: string;
    session_id: string;
}

export interface IReverePaySettings {
    redirect_url: string;
    tokenization_key: string;
}

export interface IMolliePaySettings {
    checkout_url: string;
}

export interface IInvoicePaySettings {
    redirect_url: string;
}

export interface IIngenicoPaySettings {
    isSuccess: boolean;
    hostedCheckoutId: string;
    redirectUrl: string;
}

export type IPayOnePaySettings = IIngenicoPaySettings & {
    errors?: string[],
}

export const enum MonerisPaySettingsEnvironment {
    qa = 'qa',
    prod = 'prod'
}
export interface IMonerisPaySettings {
    environment: MonerisPaySettingsEnvironment;
    integration_id: string;
    success: boolean;
    ticket?: string;
}

export const enum MonerisPaymentArgsHandler {
    paymentReceipt = 'payment_receipt'
}

export const enum MonerisPaymentResponseCode {
    success = '001'
}
export interface IMonerisPaymentArgs {
    handler: MonerisPaymentArgsHandler;
    ticket: string;
    response_code: MonerisPaymentResponseCode;
}

export interface IReverePaymentResponse {
    token: string;
    // TODO: There are others like card: {number: string, exp: string, type: 'visa'...},
}

export const enum ProductType {
    ticket = 'ticket',
    custom = 'custom',
    donation = 'donation',
    session = 'session'
}

export interface IPaymentIntegrationWebhookRequest {
    order_id: string;
    transaction_id: string;
    ticket: string;
}

// Orders
export interface IOrderParent {
    type: 'Event';
    id: string;
}

export interface IOrderItemTax {
    id: string;
    name: string;
    percentage: number;
}

export interface IOrderItemCancellationTerm {
    from: number;
    percentage: number;
}

export interface IOrderItemCancellationTerms {
    id: string;
    name: string;
    terms: IOrderItemCancellationTerm[];
}

export interface IOrderProduct extends Required<IStringIdEntity> {
    name: string;
    category: string;
    type: ProductType;
    price: number;
    currency: string;
    more_than_one: boolean;
    /** @defaultValue 0 */
    min_per_buyer: number;
    max_per_buyer: number;
    tax_id: string;
    cancellation_terms_id: string;
}

export interface IOrderItem extends TransactionItem {
    quantity: number;
    discount: number;
    subtotal: number;
    total: number;
    product: IOrderProduct;
    cancellation_terms: IOrderItemCancellationTerms;
    cancellation_cost_per_item: number;
    tax: IOrderItemTax;
}

export interface IOrderTransaction extends Omit<Transaction, 'created_at'> {
    id: string;
    created_at: string;
    items: IOrderItem[];
    discount: number;
    subtotal: number;
    total: number;
}

export interface IOrder extends Omit<Order, 'transactions'> {
    payment_status: string;
    participant_id: string;
    parent: IOrderParent;
    subtotal: number;
    total: number;
    discount: number;
    first_name: string;
    last_name: string;
    email: string;
    contact_first_name: string;
    contact_last_name: string;
    contact_email: string;
    buyer_first_name: string;
    buyer_last_name: string;
    buyer_email: string;
    transactions?: IOrderTransaction[];
}

export interface IAsset extends IStringIdEntity {
    name: string;
    content_type: string;
    file_url: string;
    thumbnail_url: string;
    file_size: number;
    filename: string;
}

export interface IGetDocumentDownloadTokenResponse {
    token: string;
}

export type PayResponseSettings = ICyberSourcePaySettings
    | IStripePaySettings
    | IInvoicePaySettings
    | IMonerisPaySettings
    | IIngenicoPaySettings
    | IMolliePaySettings;


export interface IProfileEvent {
    id: string;
    name: string;
    start: string;
    end: string;
    timezone: string;
    description: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    settings?: IEventSettings;
    contact_id: string;
    participant_id: string;
    can_approve?: boolean;
    can_impersonate?: boolean;
    stage_id: string;
}

export interface ISearchProfileEventsResponse extends IPagedResponse {
    events: IProfileEvent[];
}

/**
 * `1` == Deny the approver
 * `0` == Approved by approver
 */
export enum ParticipantStageActionDenyType {
    Approve,
    Deny
}

export interface ISearchProfileContactsResponse extends IPagedResponse {
    contacts: IProfileContact[];
}

export enum PaymentStatus {
    pending = 'pending'
}

export interface IProfileOrder {
    id: string;
    reference_number: string;
    status: PaymentStatus;
    parent: {
        type: 'Event';
        id: string;
    };
    amount: number;
    discount: number;
    subtotal: number;
}

export interface IGetProfileOrdersResponse extends IPagedResponse {
    orders: IProfileOrder[];
}

export const enum ProfilePageSectionLayout {
    oneColumn = '1-column',
    twoColumns = '2-column'
}

export interface IProfilePageSection {
    layout: ProfilePageSectionLayout;
    label: string;
    field_ids: string[];
}

export interface IProfilePage extends IStringIdEntity {
    name: string;
    default: boolean;
    sections?: IProfilePageSection[];
}

export interface IProfilePageResponse {
    contact: { [key: string]: any };
    fields: IField[];
    page: IProfilePage;
}

export interface IImageFieldOptions {
    cropping_mandatory: boolean;
    aspect_ratio: number;
    maxWidth: number;
    maxHeight: number;
}

export interface IRatingFieldOptions {
    visualStyle: RatingFieldOptionVisualStyle;
}

export interface ISortField {
    sort_field?: {
        field_name: string;
        direction: SortDirection;
    };
}

export interface ISearchEventsPayload extends ISortField {
    my_organization: boolean;
    event_filter: any,
    participant_filter: any
}

export interface IProfileContact extends IStringIdEntity {
    first_name: string;
    last_name: string;
    email: string;
}

export interface ISearchContactsPayload extends ISortField {
    contact_filter: any;
}

export interface ISearchEventActivitiesResponse extends IPagedResponse {
    activities: IEventActivity[];
    groups: IActivityGroup[];
}

export const enum EventActivityStatus {
    active = 'active',
    inactive = 'inactive'
}

export interface IEventActivity extends IStringIdEntity {
    name: string;
    start: string;
    end: string;
    description: string;
    url?: string;
    registration: boolean;
    // display: EventActivityDisplay[];
    status: EventActivityStatus;
    summary?: string;
    details?: string;
    liked: boolean;
    speaker_info?: string;
    room?: string;
}

// export const enum ActivityParticipantStatus {
//     new = 'new',
//     waitingList = 'waiting-list',
//     paymentInProgress = 'payment-in-progress',
//     registrationInProgress = 'registration-in-progress',
//     approvalPending = 'approval-pending',
//     registered = 'registered',
//     arrived = 'arrived',
//     noShow = 'no-show'
// }

// export interface ICartActivity extends ICreatedAtUpdatedAt {
//     id: string;
//     name: string;
//     start: string;
//     end: string;
//     description: string;
//     like: boolean;
//     activity_participant_status: ActivityParticipantStatus;
//     places_left?: number;
//     waitinglist?: boolean;
// }

// export interface ICartActivity {
//     activity: IActivity;
// }

// export interface IActivityGroup extends IStringIdEntity {
//     name: string;
// }

// export interface IActivityCondition extends IStringIdEntity {
//     name: string;
//     maximum: number;
// }

export interface IGetActivitiesCartResponse {
    // activities: ICartActivity[];
    activities: IActivity[];
    groups: IActivityGroup[];
    conditions: IActivityCondition[];
}

export const enum SortDirection {
    asc = 'asc',
    desc = 'desc'
}

export interface IAdvancedSearchRequest extends IPagedRequest {
    quickSearchText?: string;
    sortFieldName?: string;
    sortDirection?: SortDirection;
    advancedQuery?: SearchParams;
}

export interface IPagingOptions {
    pageSizes: number[];
    object_count: number;
    limit: number;
    offset: number;
}

export interface IRegisterToActivitiesRequest {
    activity_ids: string[];
}


export interface IContactEmailCampaignSubscription {
    email_campaign_type_id: string;
    email_campaign_type_name: string;
    subscribed: boolean;
}

export interface IContactEmailCampaignSubscriptions {
    unsubscribe: boolean;
    subscriptions: IContactEmailCampaignSubscription[];
}

export interface IGroup<T> {
    key: string;
    items: T[];
}

export const enum EventAccess {
    public = 'public',
    invited = 'invited',
    login = 'login'
}

export const enum LoginMethod {
    azavista = 'azavista',
    accesscode = 'accesscode',
    identityprovider = 'identityprovider',
    createaccount = 'createaccount',
    loginlink = 'loginlink',
}

export interface IImpersonalizationTokenPayload {
    email: string,
    username: string,
    contact_id: string,
    cl: string, // "AZAVISTA-IDENTITY",
    t: string, // "auth",
    confirmed: boolean,
    iat: number;
    exp: number;
}
export interface IImpersonateResponse {
    email: string;
    impersonization_token: IGetTokenResponse;
}

export interface IShoppingCartChangedData {
    discountCode?: string;
    discount?: IDiscount;
    productsWithQuantity: IProductWithQuantity[];
    selectedPayByOption?: PayById;
    currentOrderTotal?: number;
    cartCurrency?: string;
}

export interface IProductWithQuantity {
    product: IPaymentProduct;
    quantity: number;
}

export interface IProductVisibility {
    productId: string;
    hidden: boolean;
}

export const enum PayById {
    invoice = 'invoice',
    online = 'online'
}

export const enum LanguageDirection {
    ltr = 'ltr',
    rtl = 'rtl',
}
